// ---------------------------------------------------------------------------
// why-now-graph - the authored graph behind the landing's why-now
// fixture. The five tool cards dissolve into coloured dots, and those dots are
// the graph's nodes: each card owns a slice of the real structure, so the
// assembly is a re-parenting of what the viewer already saw, not a new picture.
//
// Reveal order is BREADTH-FIRST FROM THE ROOT (`naumu` at step 0, then ring by
// ring outward), which is what the choreography plays back: the memory appears
// first and the copies attach to it.
//
// Edge convention matches every marketing graph: source = CHILD,
// target = PARENT. A node and the edge up to its parent share a reveal step.
// ---------------------------------------------------------------------------

import { edgeKey } from './build-reveal-plan';
import type { MarketingGraphEdge, MarketingGraphNode } from './types';

/**
 * Where a node comes from BEFORE the tools arrive: the website that opens the
 * fixture, or the files dropped on it straight after. Everything without
 * an origin belongs to the tool cards and joins during the assembly.
 */
export type WhyNowOrigin = 'web' | 'files';
export type WhyNowCardId = 'hubspot' | 'slack' | 'jira' | 'asana' | 'notion';

export const WHY_NOW_ROOT_ID = 'naumu';

interface AuthoredNode extends MarketingGraphNode {
	/** Omitted for a branch head - it hangs off the Naumu root. */
	parentId?: string;
	/**
	 * Written by the agent in beat 3. The node sits in the simulation from the
	 * start (so its slot in the layout is reserved and nothing is re-created
	 * mid-story) but stays invisible until its comet lands.
	 */
	agentBorn?: boolean;
	/** Born in the opening acts, ahead of the tool cards - see WhyNowOrigin. */
	origin?: WhyNowOrigin;
}

type Leaf = [id: string, label: string, type: string, origin?: WhyNowOrigin];

/** One branch: a head (off its area, or off the root) and the leaves that ring it. */
const branch = (head: AuthoredNode, leaves: Leaf[]): AuthoredNode[] => [
	head,
	...leaves.map(([id, label, type, origin]) => ({ id, label, type, origin, parentId: head.id })),
];

// Three levels, like a real space: five AREAS round the root, each area's
// branches one hop out, and every branch a head with its leaves fanned round it.
// Thirteen branches straight off the root read as a dandelion, not a memory;
// deeper than this (root > area > branch > task > bug) reads as a knot - four
// hops of edges have to cross their neighbours to fit. Only two chains go a hop
// further, because the agent beat reads along them: launch copy > pricing page,
// and the task the agent writes > its PR.
//
// Area order is RING order (areas fan round the root in this sequence), and
// the three sources are dealt round it in turn - website, file, tool, website...
// - so every act lands round the tile instead of piling onto one side. An area
// is born in the EARLIEST act any of its branches needs, so nothing ever hangs
// off a node that is not on stage yet.
const AUTHORED_NODES: AuthoredNode[] = [
	// The areas, in ring order. `2026 goals` further down is the fifth: a branch
	// of its own, straight off the root.
	{ id: 'area-offering', label: 'Offering', type: 'Service', origin: 'web' },
	{ id: 'area-clients', label: 'Clients', type: 'Segment', origin: 'files' },
	{ id: 'area-roadmap', label: 'Roadmap', type: 'Feature' },
	{ id: 'area-team', label: 'Team', type: 'Department', origin: 'web' },
	// Website: the services page. The dropped deck hangs its processes on it.
	...branch({ parentId: 'area-offering', id: 'page-services', label: 'Services', type: 'Service', origin: 'web' }, [
		['service-strategy', 'Strategy', 'Service', 'web'],
		['service-design', 'Design', 'Service', 'web'],
		['service-build', 'Build', 'Service', 'web'],
		['process-discovery', 'Discovery', 'Process', 'files'],
		['process-handoff', 'Handoff', 'Process', 'files'],
	]),
	// Jira's card, with the chat and doc dots that belong to the same launch.
	...branch({ parentId: 'area-roadmap', id: 'feature-launch', label: 'Launch readiness', type: 'Feature' }, [
		['task-release-plan', 'Release plan', 'Task'],
		['task-beta-signoff', 'Beta sign-off', 'Task'],
		['task-launch-copy', 'Launch copy', 'Task'],
		['bug-date-sync', 'Date sync', 'Bug'],
		['message-launch-moved', 'Launch moved', 'Message'],
		['doc-launch-brief', 'Launch brief', 'Document'],
	]),
	{ id: 'doc-pricing-page', label: 'Pricing page', type: 'Document', parentId: 'task-launch-copy' },
	{ id: 'task-user-ratings', label: 'User ratings', type: 'Task', parentId: 'feature-launch', agentBorn: true },
	{ id: 'doc-ratings-pr', label: 'PR #412', type: 'Document', parentId: 'task-user-ratings', agentBorn: true },
	// Deck.pdf: the year's goals.
	...branch({ id: 'goal-2026', label: '2026 goals', type: 'Goal', origin: 'files' }, [
		['goal-revenue', 'Revenue', 'Goal', 'files'],
		['goal-hiring', 'Hiring', 'Goal', 'files'],
		['goal-launch', 'EU launch', 'Goal', 'files'],
		['metric-arr', 'ARR target', 'Metric', 'files'],
		['metric-headcount', 'Headcount', 'Metric', 'files'],
		['market-dach', 'DACH', 'Market', 'files'],
	]),
	// Website: the team page, one branch per department.
	...branch({ parentId: 'area-team', id: 'department-product', label: 'Product', type: 'Department', origin: 'web' }, [
		['person-ana', 'Ana', 'Person', 'web'],
		['person-sarah', 'Sarah', 'Person', 'web'],
		['role-ana', 'Head of product', 'Role', 'files'],
		['message-ooo', 'OOO until May 24', 'Message'],
	]),
	// HubSpot's card, split over two branches: the partner...
	...branch({ parentId: 'area-clients', id: 'partner-arden', label: 'Arden Partners', type: 'Partner' }, [
		['client-klarwerk', 'Klarwerk', 'Client'],
		['task-klarwerk-brief', 'Klarwerk brief', 'Task'],
		['note-renewal-link', 'Renewal', 'Note'],
		['doc-renewal-notes', 'Renewal notes', 'Document'],
	]),
	// Clients.csv: the client list, ahead of the CRM that later joins it.
	...branch({ parentId: 'area-clients', id: 'segment-key-accounts', label: 'Key accounts', type: 'Segment', origin: 'files' }, [
		['client-northwind', 'Northwind', 'Client', 'files'],
		['client-halden', 'Halden', 'Client', 'files'],
		['client-orbit', 'Orbit', 'Client', 'files'],
		['client-fennel', 'Fennel', 'Client', 'files'],
		['contact-northwind', 'Ops lead', 'Person', 'files'],
		['contact-halden', 'Founder', 'Person', 'files'],
	]),
	// Website: the pricing page.
	...branch({ parentId: 'area-offering', id: 'page-pricing', label: 'Pricing', type: 'Plan', origin: 'web' }, [
		['plan-starter', 'Starter', 'Plan', 'web'],
		['plan-team', 'Team', 'Plan', 'web'],
		['addon-support', 'Support add-on', 'Plan', 'files'],
	]),
	// Asana's card.
	...branch({ parentId: 'area-roadmap', id: 'feature-collaboration', label: 'Collaboration', type: 'Feature' }, [
		['task-shared-inbox', 'Shared inbox', 'Task'],
		['task-mentions', 'Mentions', 'Task'],
		['bug-unread-count', 'Unread count', 'Bug'],
		['bug-mobile-state', 'Mobile state', 'Bug'],
		['message-beta-ready', 'Beta ready', 'Message'],
		['note-thread-link', 'Thread', 'Note'],
	]),
	// Handbook.docx.
	...branch({ parentId: 'area-team', id: 'doc-handbook', label: 'Handbook', type: 'Policy', origin: 'files' }, [
		['policy-security', 'Security', 'Policy', 'files'],
		['policy-leave', 'Leave policy', 'Policy', 'files'],
		['ritual-weekly', 'Weekly review', 'Ritual', 'files'],
		['note-team-link', 'Handover', 'Note', 'files'],
	]),
	...branch({ parentId: 'area-team', id: 'department-growth', label: 'Growth', type: 'Department', origin: 'web' }, [
		['person-niko', 'Niko', 'Person', 'web'],
		['person-maya', 'Maya', 'Person', 'web'],
		['role-maya', 'Growth lead', 'Role', 'files'],
		['doc-onboarding', 'Onboarding', 'Document'],
	]),
	// ...and the client the agent later reads.
	...branch({ parentId: 'area-clients', id: 'client-meridian', label: 'Meridian', type: 'Client' }, [
		['task-meridian-demo', 'Meridian demo', 'Task'],
		['message-demo-date', 'Demo date', 'Message'],
		['doc-renewal-checklist', 'Checklist', 'Document'],
	]),
	// Clients.csv, continued: what is being sold to them.
	...branch({ parentId: 'area-clients', id: 'pipeline-open', label: 'Open deals', type: 'Deal', origin: 'files' }, [
		['deal-orbit', 'Orbit retainer', 'Deal', 'files'],
		['deal-fennel', 'Fennel pilot', 'Deal', 'files'],
		['deal-halden', 'Halden renewal', 'Deal', 'files'],
	]),
	// Website: the case studies.
	...branch({ parentId: 'area-offering', id: 'page-case-studies', label: 'Case studies', type: 'Story', origin: 'web' }, [
		['story-northwind', 'Northwind relaunch', 'Story', 'web'],
		['story-halden', 'Halden rebrand', 'Story', 'web'],
		['story-meridian', 'Meridian rollout', 'Story', 'files'],
	]),
];

const NODE_TYPE_BY_ID = new Map(AUTHORED_NODES.map((node) => [node.id, node.type]));

export interface WhyNowCardDot {
	/** Graph node this dot becomes - the seed position for the assembly. */
	id: string;
	type: string;
	/** Position inside the card frame, in percent. */
	x: number;
	y: number;
	/** Radius in px; message and doc dots read smaller than structural nodes. */
	r: number;
}

const dot = (id: string, x: number, y: number, r = 3.5): WhyNowCardDot => ({
	id,
	type: NODE_TYPE_BY_ID.get(id) ?? 'Note',
	x,
	y,
	r,
});

/**
 * Which dots each card holds at the 50% mark, and where they sit inside its
 * frame. HubSpot carries the CRM region, Jira and Asana split product, and
 * Slack and Notion scatter across all three - chat and docs are the solvent.
 */
export const WHY_NOW_CARD_DOTS: Record<WhyNowCardId, WhyNowCardDot[]> = {
	hubspot: [
		dot('partner-arden', 26, 34, 4.5),
		dot('client-meridian', 62, 26),
		dot('client-klarwerk', 78, 62),
		dot('task-meridian-demo', 41, 68),
		dot('task-klarwerk-brief', 17, 74),
	],
	jira: [
		dot('feature-launch', 30, 30, 4.5),
		dot('task-release-plan', 58, 22),
		dot('task-beta-signoff', 72, 55),
		dot('task-launch-copy', 24, 66),
		dot('bug-date-sync', 47, 76),
	],
	asana: [
		dot('feature-collaboration', 34, 62, 4.5),
		dot('task-shared-inbox', 66, 28),
		dot('task-mentions', 22, 26),
		dot('bug-unread-count', 78, 72),
		dot('bug-mobile-state', 52, 44),
	],
	slack: [
		dot('message-launch-moved', 30, 34, 3),
		dot('message-beta-ready', 64, 24, 3),
		dot('message-demo-date', 46, 62, 3),
		dot('message-ooo', 76, 70, 3),
	],
	notion: [
		dot('doc-launch-brief', 28, 30, 3),
		dot('doc-pricing-page', 62, 38, 3),
		dot('doc-renewal-notes', 40, 66, 3),
		dot('doc-renewal-checklist', 76, 26, 3),
		dot('doc-onboarding', 62, 74, 3),
	],
};

export interface WhyNowGraph {
	nodes: MarketingGraphNode[];
	parentEdges: MarketingGraphEdge[];
	meshEdges: MarketingGraphEdge[];
	/**
	 * Breadth-first order from the root WITHIN a node's own act - the website
	 * read, the file drop and the tool assembly each count from 1 - so every
	 * act's flight stagger starts at once instead of waiting out the others.
	 */
	nodeRevealIndex: Map<string, number>;
	edgeRevealIndex: Map<string, number>;
	parentOf: Map<string, string>;
	/** Position among siblings, and how many there are - fans the flight targets. */
	siblingIndex: Map<string, number>;
	siblingCount: Map<string, number>;
	/** Nodes the agent writes in beat 3 - hidden until then. */
	agentBornIds: Set<string>;
	/** Nodes born before the tool cards, and which opening act brings each. */
	originById: Map<string, WhyNowOrigin>;
	/** Highest reveal index per act - when that act's last node launches. */
	originLastRevealIndex: Map<WhyNowOrigin, number>;
	/** Reveal steps in the tool assembly, root included. */
	total: number;
}

/**
 * Breadth-first from the root: step 0 is the Naumu tile alone, then each ring
 * of children with the edge that attaches them. Authored order inside a ring
 * interleaves the three regions so the graph blooms instead of unrolling one
 * region at a time.
 */
export const buildWhyNowGraph = (): WhyNowGraph => {
	const childrenOf = new Map<string, AuthoredNode[]>();
	for (const node of AUTHORED_NODES) {
		const parentId = node.parentId ?? WHY_NOW_ROOT_ID;
		childrenOf.set(parentId, [...(childrenOf.get(parentId) ?? []), node]);
	}

	const nodes: MarketingGraphNode[] = [
		{ id: WHY_NOW_ROOT_ID, label: 'Naumu', type: 'Naumu' },
	];
	const parentEdges: MarketingGraphEdge[] = [];
	const nodeRevealIndex = new Map<string, number>([[WHY_NOW_ROOT_ID, 0]]);
	const edgeRevealIndex = new Map<string, number>();
	const parentOf = new Map<string, string>();
	const siblingIndex = new Map<string, number>();
	const siblingCount = new Map<string, number>();

	const originById = new Map<string, WhyNowOrigin>();
	const originLastRevealIndex = new Map<WhyNowOrigin, number>();
	const steps: Record<WhyNowOrigin | 'tools', number> = { web: 0, files: 0, tools: 0 };

	const queue: string[] = [WHY_NOW_ROOT_ID];
	while (queue.length > 0) {
		const parentId = queue.shift() as string;
		const siblings = childrenOf.get(parentId) ?? [];
		siblings.forEach((node, index) => {
			siblingIndex.set(node.id, index);
			siblingCount.set(node.id, siblings.length);
		});
		for (const node of siblings) {
			const act = node.origin ?? 'tools';
			steps[act] += 1;
			const step = steps[act];
			parentOf.set(node.id, parentId);
			nodes.push({ id: node.id, label: node.label, type: node.type });
			nodeRevealIndex.set(node.id, step);
			if (node.origin) {
				originById.set(node.id, node.origin);
				originLastRevealIndex.set(node.origin, step);
			}
			parentEdges.push({ source: node.id, target: parentId, isParent: true });
			edgeRevealIndex.set(edgeKey(node.id, parentId), step);
			queue.push(node.id);
		}
	}

	return {
		nodes,
		parentEdges,
		meshEdges: [],
		nodeRevealIndex,
		edgeRevealIndex,
		parentOf,
		siblingIndex,
		siblingCount,
		agentBornIds: new Set(AUTHORED_NODES.filter((node) => node.agentBorn).map((node) => node.id)),
		originById,
		originLastRevealIndex,
		total: steps.tools + 1,
	};
};
